import { ReactNode, useState, useEffect } from 'react';
import useUnleashFeatureFlagAdapter from '../../adapter/UnleashFeatureFlagAdapter';

export type UseUserFeatureFlagResult = {
  handleRenderByFeatureFlag: (
    component: ReactNode,
    defaultComponent: ReactNode,
  ) => ReactNode;
  isFeatureFlagEnable: boolean;
  flagsReady: boolean;
};

const useUserFeatureFlag = (featureFlag?: string): UseUserFeatureFlagResult => {
  const { isUserEnabled, flagsReady } = useUnleashFeatureFlagAdapter();
  const [isFeatureFlagEnable, setIsFeatureFlagEnable] = useState(false);

  useEffect(() => {
    let mounted = true;

    const checkFlag = async () => {
      try {
        const enabled = await isUserEnabled(featureFlag!);
        if (mounted) setIsFeatureFlagEnable(enabled);
      } catch (error) {
        console.error(
          'Failed to check user feature flag:',
          featureFlag,
          error,
        );
        if (mounted) setIsFeatureFlagEnable(false);
      }
    };

    if (featureFlag) {
      void checkFlag();
    }

    return () => {
      mounted = false;
    };
  }, [featureFlag, isUserEnabled]);

  const handleRenderByFeatureFlag = (
    component: ReactNode,
    defaultComponent: ReactNode,
  ): ReactNode => {
    return isFeatureFlagEnable ? component : defaultComponent;
  };

  return { handleRenderByFeatureFlag, isFeatureFlagEnable, flagsReady };
};

export default useUserFeatureFlag;
